// The signed-in user's own account and preferences (issue #378).
// Shapes mirror the backend's GET /api/v1/me response (#377).

import { UserRole, UserStatus } from './adminUser';
import { OWNERSHIP_FILTERS, OwnershipFilter } from './drone';

/**
 * What the user *chose*. `system` is not a colour — it defers to the OS, so it
 * has to stay distinct from the resolved theme all the way through. Collapsing
 * the two is what makes a "system" choice silently become a fixed light or dark
 * the next time it is saved.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

/** All selectable preferences, in the order the settings screen offers them. */
export const THEME_PREFERENCES: readonly ThemePreference[] = [
  'light',
  'dark',
  'system',
] as const;

/**
 * Which drones the fleet view opens on (#385).
 *
 * Saved so the operator's choice *is* the default from then on, rather than the
 * view resetting to `all` on every reload. `all` remains the first-visit
 * default, because an unclaimed aircraft can be airborne and a view that hides
 * it is one nobody is watching — but that is a starting point, not a policy the
 * operator has to live with.
 */
export interface FleetPreferences {
  ownership: OwnershipFilter;
  /** How the Fleet page lays the drones out (#412, G7). */
  layout: FleetLayout;
}

/**
 * The Fleet page's two layouts (#412): rows with a thumbnail, or tiles with a
 * bigger image and the name below.
 */
export type FleetLayout = 'list' | 'grid';

/** The selectable layouts, in the order the toggle offers them. */
export const FLEET_LAYOUTS: readonly FleetLayout[] = ['list', 'grid'] as const;

/** Planner defaults a new mission draft starts from. */
export interface PlannerPreferences {
  cruiseSpeedMs: number;
  altitudeM: number;
}

/**
 * The preferences document. The backend stores this opaquely, so this interface
 * is the only definition of its shape — adding a setting is a change here and
 * nowhere on the server.
 */
export interface UserPreferencesPayload {
  theme: ThemePreference;
  planner: PlannerPreferences;
  fleet: FleetPreferences;
}

/** The stored document as the API returns it. */
export interface StoredPreferences {
  subject: string;
  /**
   * Whatever was stored, which is not necessarily a current document (#510).
   *
   * `Partial`, not the full payload: the backend treats this as opaque JSON
   * (`json.RawMessage`) and returns it unchanged, so a document written by an
   * older UI is missing every block added since. Declaring it complete was a
   * type asserting something the server does not guarantee — the same category
   * of lie that crashed the fleet map in #508 — and it forced the tests that
   * cover the partial case to cast their way past the compiler.
   *
   * {@link normalizePreferences} is what turns this into a usable document, and
   * it already takes a Partial.
   */
  payload: Partial<UserPreferencesPayload>;
  updated_at?: string;
}

/** The caller's own profile. */
export interface UserProfile {
  subject: string;
  email: string;
  display_name: string;
  status: UserStatus;
  created_at: string;
  last_login_at: string | null;
}

/** GET /api/v1/me — profile, roles and preferences in one call. */
export interface MeResponse {
  user: UserProfile;
  roles: UserRole[];
  preferences: StoredPreferences;
}

/**
 * Defaults mirroring the backend's own seed, so a first paint before the
 * response arrives looks identical to the one after it.
 */
export const DEFAULT_PREFERENCES: UserPreferencesPayload = {
  theme: 'system',
  planner: { cruiseSpeedMs: 10, altitudeM: 50 },
  fleet: { ownership: 'all', layout: 'grid' },
};

/**
 * Coerces whatever the server returned into a usable preferences document.
 *
 * The payload is opaque to the backend, so it can legitimately predate a
 * setting, come from an older UI, or be hand-edited. Anything missing or of the
 * wrong type falls back to the default rather than reaching a component that
 * assumes it is there — a bad stored value must not be able to break the
 * settings screen that would let a user fix it.
 */
export const normalizePreferences = (
  payload: Partial<UserPreferencesPayload> | null | undefined
): UserPreferencesPayload => {
  const theme = payload?.theme;
  const planner = payload?.planner;
  const fleet = payload?.fleet;
  return {
    theme: THEME_PREFERENCES.includes(theme as ThemePreference)
      ? (theme as ThemePreference)
      : DEFAULT_PREFERENCES.theme,
    planner: {
      cruiseSpeedMs:
        typeof planner?.cruiseSpeedMs === 'number' && planner.cruiseSpeedMs > 0
          ? planner.cruiseSpeedMs
          : DEFAULT_PREFERENCES.planner.cruiseSpeedMs,
      altitudeM:
        typeof planner?.altitudeM === 'number' && planner.altitudeM > 0
          ? planner.altitudeM
          : DEFAULT_PREFERENCES.planner.altitudeM,
    },
    fleet: {
      // An unrecognised stored value falls back to `all` rather than to an
      // empty map: a preferences document written by a future UI must not be
      // able to hide the fleet from an older one.
      ownership: OWNERSHIP_FILTERS.includes(fleet?.ownership as OwnershipFilter)
        ? (fleet?.ownership as OwnershipFilter)
        : DEFAULT_PREFERENCES.fleet.ownership,
      layout: FLEET_LAYOUTS.includes(fleet?.layout as FleetLayout)
        ? (fleet?.layout as FleetLayout)
        : DEFAULT_PREFERENCES.fleet.layout,
    },
  };
};
